import type { PagePlatform, PageType } from '@teo/widget-shared';
import {
  isHumanGateEnabled,
  isHumanReady,
  subscribeHumanReady,
} from '../../human-gate/index.js';
import { collectPageContext } from '../parse-page-context.js';
import { watchPageNavigation } from '../watch-page-navigation.js';
import {
  cartSnapshotChanged,
  getCartSnapshot,
  resetCartState,
  setCartSnapshot,
} from './cart-state.js';
import { collectCartSnapshotAsync } from './collect-cart-snapshot.js';
import { reportCartUpdated, type CartUpdatedReason } from './report-cart-updated.js';

const DEBOUNCE_MS = 400;
const HEADER_OBSERVE_ROOT_SELECTORS =
  'header, nav, [role="banner"], [class*="header" i], [id*="header" i], body';

let started = false;
let stopWatchers: (() => void) | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingReason: CartUpdatedReason = 'change';
/** Snapshot change while gated — flush on human_ready. */
let pendingEmit = false;

function resolvePageMeta(): { pageType?: PageType; platform?: PagePlatform } {
  if (typeof window === 'undefined') return {};
  try {
    const context = collectPageContext({
      url: window.location.href,
      skipBehavior: true,
      skipCart: true,
    });
    return {
      pageType: context.pageType,
      platform: context.platform,
    };
  } catch {
    return {};
  }
}

async function refreshCart(reason: CartUpdatedReason): Promise<void> {
  if (typeof window === 'undefined') return;

  const meta = resolvePageMeta();
  const next = await collectCartSnapshotAsync({
    pageType: meta.pageType,
    platform: meta.platform,
  });

  const prev = getCartSnapshot();
  if (!cartSnapshotChanged(prev, next)) {
    if (next) setCartSnapshot(next);
    return;
  }

  setCartSnapshot(next);
  if (!next) return;

  if (isHumanGateEnabled() && !isHumanReady()) {
    pendingEmit = true;
    pendingReason = reason;
    return;
  }

  reportCartUpdated(next, reason);
  pendingEmit = false;
}

function scheduleRefresh(reason: CartUpdatedReason): void {
  pendingReason = reason;
  if (debounceTimer != null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void refreshCart(pendingReason);
  }, DEBOUNCE_MS);
}

function patchDataLayerPush(): () => void {
  if (typeof window === 'undefined') return () => {};
  const layer = window.dataLayer;
  if (!Array.isArray(layer)) return () => {};

  const originalPush = layer.push.bind(layer);
  layer.push = (...args: unknown[]) => {
    const result = originalPush(...(args as never[]));
    scheduleRefresh('change');
    return result;
  };

  return () => {
    if (window.dataLayer === layer) {
      layer.push = originalPush;
    }
  };
}

function observeHeaderBadge(): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const roots = Array.from(
    document.querySelectorAll(HEADER_OBSERVE_ROOT_SELECTORS),
  );
  const target = roots[0] ?? document.body;
  if (!target) return () => {};

  const observer = new MutationObserver(() => {
    scheduleRefresh('change');
  });
  observer.observe(target, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      'data-cart-count',
      'data-cart-quantity',
      'data-item-count',
      'data-count',
      'class',
    ],
  });

  return () => observer.disconnect();
}

function attachCartWatchers(): () => void {
  resetCartState();
  void refreshCart('refresh');

  const stopPush = patchDataLayerPush();
  const stopHeader = observeHeaderBadge();
  const stopNav = watchPageNavigation({
    onNavigate: () => {
      scheduleRefresh('refresh');
    },
  });

  const unsubscribeHuman = subscribeHumanReady(() => {
    if (!pendingEmit) return;
    const snap = getCartSnapshot();
    if (!snap) {
      pendingEmit = false;
      return;
    }
    reportCartUpdated(snap, pendingReason);
    pendingEmit = false;
  });

  return () => {
    if (debounceTimer != null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    stopPush();
    stopHeader();
    stopNav();
    unsubscribeHuman();
  };
}

/**
 * Start cart collectors + analytics. Idempotent.
 * Install from bootstrap (independent of CONTEXT_ENABLED).
 */
export function startCartWatchers(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (started) return () => {};

  started = true;
  stopWatchers = attachCartWatchers();
  return () => stopCartWatchers();
}

export function stopCartWatchers(): void {
  stopWatchers?.();
  stopWatchers = null;
  started = false;
  resetCartState();
  pendingEmit = false;
}

export function isCartWatchersActive(): boolean {
  return started;
}

/** Test helper: force an immediate recollection. */
export async function refreshCartSnapshotNow(
  reason: CartUpdatedReason = 'refresh',
): Promise<void> {
  await refreshCart(reason);
}
